import { computeEquivalency, computeLicensedEquivalency } from "./equivalency";
import { computeOccupancy } from "./occupancy";
import { computeMatAdjPremium, computeSirAdjPremium, computeUnadjPremium } from "./premium";
import { lookupMatureRate } from "./rates";
import { lookupSirCredit } from "./sir-credit";
import type { FacilityCalcInputs, FacilityCalcRefs, FacilityCalcResult } from "./types";

/**
 * Run the full pricing chain for a single facility row.
 *
 *   1. Equivalency         — Σ(census × relativity), 4 buckets
 *   2. Licensed Equivalency — Σ(licensed × relativity), 3 buckets
 *   3. Occupancy           — Equivalency / Licensed Equivalency
 *      (Total Lic. shown in the Schedule is the PLAIN sum of the 3
 *      licensed buckets — no relativity weighting; the weighted
 *      Licensed Equivalency only feeds the Occupancy denominator)
 *   4. Rate                — `rateOverride` when the row carries one,
 *      otherwise the mature actuarial rate for the state code
 *   5. Unadj. Premium      — Equivalency × Rate
 *   6. Mat. Adj. Premium   — Unadj × maturityCredit
 *   7. SIR Credit          — `sirCreditOverride` when the row carries one,
 *      otherwise a tiered lookup on the retention amount
 *   8. SIR Adj. Premium    — Mat. Adj. × (1 − sirCredit)
 *
 * The function stays pure: every reference table (rates, SIR, the
 * relativity factors themselves) is passed in via `refs`. The React
 * hook layer wires those up from cached endpoint state; tests pass
 * them inline.
 *
 * Failures cascade as `null` so a missing piece (unknown state,
 * off-tier retention, zero licensed equivalency) shows up as a clean
 * "—" in the UI instead of surfacing `NaN`/`Infinity`.
 */
export function computeFacilityRow(
    inputs: FacilityCalcInputs,
    refs: FacilityCalcRefs,
): FacilityCalcResult {
    const equivalency = computeEquivalency(inputs.census, refs.relativity);
    const licensedEquivalency = computeLicensedEquivalency(inputs.licensed, refs.relativity);
    const occupancy = computeOccupancy(equivalency, licensedEquivalency);
    const totalLicensed = inputs.licensed.snf + inputs.licensed.alf + inputs.licensed.ilf;
    // `??`, not `||`: a stored 0 is a real rate and must win over the
    // table, the same way an explicit 0 imports verbatim on the backend.
    const rate = inputs.rateOverride ?? lookupMatureRate(inputs.rateCode, refs.rates);
    const unadjPremium = computeUnadjPremium(equivalency, rate);
    const matAdjPremium = computeMatAdjPremium(unadjPremium, inputs.maturityCredit);
    // Same `??` reasoning as the rate above: a stored 0 is the table's own
    // $0 tier, not an absent credit.
    const sirCredit = inputs.sirCreditOverride ?? lookupSirCredit(inputs.retention, refs.sirTable);
    const sirAdjPremium = computeSirAdjPremium(matAdjPremium, sirCredit);
    return {
        occupancy,
        equivalency,
        rate,
        unadjPremium,
        matAdjPremium,
        sirCredit,
        sirAdjPremium,
        totalLicensed,
    };
}
